import { SupabaseClient } from "@supabase/supabase-js";
import { Profile } from "@/lib/types/auth";
import { createLogger } from "@/lib/utils/logger";

const logger = createLogger('auth', 'AuthHandlers');

export const useAuthHandlers = (supabase: SupabaseClient) => {
  // Function to check if NWSISD school district exists (read-only operation)
  const checkNWSISDExists = async () => {
    try {
      logger.info("Checking if NWSISD district exists");
      
      // Only perform a read operation to check if NWSISD exists
      const { data: existingDistrict, error: districtError } = await supabase
        .from('school_districts')
        .select('id')
        .eq('name', 'NWSISD')
        .maybeSingle();
      
      if (districtError) {
        logger.error("Error checking for NWSISD district: " + districtError.message);
        return { exists: false, error: districtError };
      }
      
      logger.info("NWSISD district exists check result: " + String(!!existingDistrict));
      
      return { 
        exists: !!existingDistrict,
        districtId: existingDistrict?.id,
      };
    } catch (error) {
      logger.error("Failed in checkNWSISDExists: " + String(error));
      return { exists: false, error };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    schoolDistrict: string,
    school: string,
    position: string,
    customSchoolDistrict: string = "",
    customSchool: string = "",
    customPosition: string = ""
  ) => {
    try {
      // Log the signup process initiation
      logger.info("Starting signup process for user: " + email);
      logger.info("Signup data: " + JSON.stringify({ 
        firstName, 
        lastName, 
        schoolDistrict, 
        school, 
        position,
        hasCustomSchoolDistrict: !!customSchoolDistrict,
        hasCustomSchool: !!customSchool,
        hasCustomPosition: !!customPosition
      }));
      
      // Just check if NWSISD exists (read-only operation) - we don't try to create it
      const { exists: nwsisdExists } = await checkNWSISDExists();
      logger.info("NWSISD district exists: " + String(nwsisdExists));

      // Use custom values when "Other" is selected
      const finalSchoolDistrict = schoolDistrict === "Other" ? customSchoolDistrict : schoolDistrict;
      const finalSchool = school === "Other" ? customSchool : school;
      
      // Extract position title from the value format: "title|category"
      let finalPosition = position === "Other" ? customPosition : position;
      if (finalPosition.includes('|')) {
        finalPosition = finalPosition.split('|')[0];
      }

      logger.info("Final values to be stored: " + JSON.stringify({
        finalSchoolDistrict,
        finalSchool,
        finalPosition
      }));

      // Set a timeout for the signup request
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Signup request timed out after 15 seconds")), 15000)
      );

      // Sign up the user with metadata in the auth.users table
      logger.info("Attempting to sign up user with Supabase auth...");
      
      // Race the signup request against a timeout
      const authDataPromise = supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            school_district: finalSchoolDistrict,
            school: finalSchool,
            position: finalPosition
          }
        }
      });
      
      // Wait for either the signup to complete or the timeout to trigger
      const { data: authData, error: signUpError } = await Promise.race([
        authDataPromise,
        timeoutPromise.then(() => { 
          throw new Error("Signup request timed out");
        })
      ]);

      if (signUpError) {
        logger.error("Signup error: " + String(signUpError));
        throw signUpError;
      }

      logger.info("Auth signup successful: " + authData.user?.id);

      // After signup, explicitly update the profile to ensure data consistency
      if (authData.user) {
        try {
          logger.info("Updating profile data in profiles table...");
          logger.info("Profile data to insert: " + JSON.stringify({
            id: authData.user.id,
            first_name: firstName,
            last_name: lastName,
            school_district: finalSchoolDistrict,
            school: finalSchool,
            position: finalPosition
          }));

          // First check if profile already exists
          const { data: existingProfile, error: checkError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', authData.user.id)
            .maybeSingle();

          if (checkError) {
            logger.error("Error checking existing profile: " + String(checkError));
            throw new Error("Failed to verify profile status. Please try again.");
          }

          let profileResult;
          if (existingProfile) {
            // Update existing profile
            logger.info("Updating existing profile...");
            profileResult = await supabase
              .from('profiles')
              .update({
                first_name: firstName,
                last_name: lastName,
                school_district: finalSchoolDistrict,
                school: finalSchool,
                position: finalPosition,
                updated_at: new Date().toISOString()
              })
              .eq('id', authData.user.id)
              .select()
              .single();
          } else {
            // Insert new profile
            logger.info("Creating new profile...");
            profileResult = await supabase
              .from('profiles')
              .insert({
              id: authData.user.id,
              first_name: firstName,
              last_name: lastName,
              school_district: finalSchoolDistrict,
              school: finalSchool,
              position: finalPosition,
              updated_at: new Date().toISOString()
              })
              .select()
              .single();
          }
      
          if (profileResult.error) {
            logger.error("Error in profile operation: " + JSON.stringify({
              error: profileResult.error,
              errorMessage: profileResult.error.message,
              details: profileResult.error.details,
              hint: profileResult.error.hint
            }));
            throw new Error("Failed to create user profile. Please try again.");
          }

          if (!profileResult.data) {
            logger.error("No profile data returned after operation");
            throw new Error("Failed to verify profile creation. Please try again.");
          }

          logger.info("Profile operation successful: " + JSON.stringify(profileResult.data));

          // Double check the profile exists and has all required fields
          const { data: verifiedProfile, error: verifyError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', authData.user.id)
            .maybeSingle();

          if (verifyError || !verifiedProfile) {
            logger.error("Profile verification failed: " + String(verifyError || "Profile not found"));
            throw new Error("Failed to verify profile creation. Please try again.");
          }

          // Verify all required fields are present
          const requiredFields = ['first_name', 'last_name', 'school_district', 'school', 'position'];
          const missingFields = requiredFields.filter(field => !verifiedProfile[field]);
          
          if (missingFields.length > 0) {
            logger.error("Profile missing required fields: " + missingFields.join(', '));
            throw new Error("Profile creation incomplete. Missing required fields.");
          }

          logger.info("Profile created and verified successfully");
        } catch (profileError) {
          logger.error("Exception in profile creation: " + String(profileError));
          // Add error details if available
          if (profileError instanceof Error) {
            logger.error("Error details: " + JSON.stringify({
              name: profileError.name,
              message: profileError.message,
              stack: profileError.stack
            }));
          }
          throw profileError;
        }
      } else {
        logger.error("No user data available after signup");
        throw new Error("Failed to create user account. Please try again.");
      }

      return authData;
    } catch (error) {
      logger.error("Signup process failed: " + String(error));
      
      // Check for specific error types
      if (error.message?.toLowerCase().includes("rate limit")) {
        const rateLimitError = new Error("Too many signup attempts. Please wait a few minutes before trying again.");
        rateLimitError.name = "RateLimitError";
        throw rateLimitError;
      }
      
      // Check for timeout error
      if (error.message?.includes("timed out")) {
        const timeoutError = new Error("The signup request is taking too long. Please try again later when the service is less busy.");
        timeoutError.name = "TimeoutError";
        throw timeoutError;
      }
      
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      logger.info("Attempting to sign in user: " + email);
      
      // Set a timeout for the signin request
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Sign in request timed out after 15 seconds")), 15000)
      );
      
      // Race the sign in request against a timeout
      const authDataPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      const { data, error } = await Promise.race([
        authDataPromise,
        timeoutPromise.then(() => { 
          throw new Error("Sign in request timed out");
        })
      ]);
      
      if (error) {
        logger.error("Sign in error: " + String(error));
        throw error;
      }
      
      logger.info("Sign in successful");
      return data;
    } catch (error) {
      logger.error("Sign in process failed: " + String(error));
      // Check for timeout error
      if (error.message?.includes("timed out")) {
        const timeoutError = new Error("The sign in request is taking too long. Please try again later when the service is less busy.");
        timeoutError.name = "TimeoutError";
        throw timeoutError;
      }
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      logger.info("Attempting to sign in with Google");
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        logger.error("Google sign in error: " + String(error));
        throw error;
      }
      
      return data;
    } catch (error) {
      logger.error("Google sign in process failed: " + String(error));
      throw error;
    }
  };

  const signOut = async () => {
    try {
      logger.info("Attempting to sign out user");
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error("Sign out error: " + String(error));
        throw error;
      }
      
      logger.info("Sign out successful");
    } catch (error) {
      logger.error("Sign out process failed: " + String(error));
      throw error;
    }
  };

  return {
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  };
};
